// PAY2-S9 (docs/build-briefs/PAY2-S9-royalty-one-view.md; DEC-405, DEC-406, DEC-420): the pure decisions behind the one
// monthly royalty view. The statement (net sales, rate, royalty due) and the charges (adjustments, amount, carried, debit
// date, dispute) for the same month are joined into one row per location. No DOM, no fetch. The server does every
// calculation; this module reads its two answers, joins them by franchisee and location name (neither reply carries an id
// of a tenant or a location), labels rate segments and formats cents. An empty value reads N/A.

import { fromServer } from "./missingValue.ts";
import { figureMoney } from "./franchisorConsoleModel.ts";
import {
  CHARGES_NA,
  toneOf,
  type DisputeRef,
  type LedgerChargeDto,
  type LocationLedgerDto,
  type NetworkDisputeDto,
  type NetworkLedgerDto,
  type Tone,
} from "./royaltyChargesModel.ts";
import type { RoyaltyStatementDto, StatementRowDto, StatementSegmentDto } from "./royaltyStatementModel.ts";

export const MONTHLY_NA = CHARGES_NA;
export const MONTHLY_FAILED = "The royalty could not be loaded. Try again.";
export const MONTHLY_DOWNLOAD_FAILED = "The file could not be downloaded. Try again.";

/** The columns, in order. The Location cell carries the franchisee's name under the location's on the franchisor view. */
export const MONTHLY_COLUMNS = [
  "Location",
  "Net sales",
  "Rate",
  "Royalty due",
  "Fund fee",
  "Adjustments",
  "Amount charged",
  "Carried",
  "Collection",
  "Dispute",
] as const;

/** The export menu's sheets, in the workbook's order. "Charges" is the file with no `export` key. */
export const MONTHLY_SHEETS: readonly { id: string; label: string }[] = [
  { id: "statement", label: "Statement" },
  { id: "charges", label: "Charges" },
  { id: "adjustments", label: "Adjustments" },
  { id: "disputes", label: "Disputes" },
];

/** The `export` query value for a picked sheet, or undefined for the default file (Charges). */
export function exportKeyFor(sheetId: string | undefined): string | undefined {
  return sheetId === undefined || sheetId === "charges" ? undefined : sheetId;
}

export interface MonthlyCollection {
  label: string;
  tone: Tone;
  /** The expected debit date, "2026-11-09", or null when the charge has none. */
  debitDate: string | null;
}

export interface MonthlyRow {
  franchisee: string;
  location: string;
  netCents: number | null;
  /** One short line per rate segment; N/A when the statement has no row. */
  rateLines: string[];
  royaltyDueCents: number | null;
  /** FRADFUND1: the brand or ad fund fee due, its own column, never inside royalty. */
  fundDueCents: number | null;
  /** The fund rate in force, "2.00%" or "Not set"; N/A when the statement has no row. */
  fundRateText: string;
  adjustmentCents: number | null;
  amountCents: number | null;
  carriedCents: number | null;
  collection: MonthlyCollection | null;
  dispute: DisputeRef | null;
  /** FRANROY1: the month is not over, so royalty is to date. */
  inProgress?: boolean;
  /** FRANROY1: "$1,200.00 a month, $1,084.00 so far" for a flat royalty. */
  flatFeeText?: string | null;
  /** The charge a dispute is filed against, on the location's own view only. */
  chargeId: string | null;
  canDispute: boolean;
}

export interface MonthlyTotals {
  netCents: number;
  royaltyDueCents: number;
  fundDueCents: number;
  adjustmentCents: number;
  amountCents: number;
  carriedCents: number;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** "Oct 18" from "2026-10-18"; anything else reads N/A. */
export function shortDay(iso: string): string {
  const match = /^\d{4}-(\d{2})-(\d{2})/.exec(iso);
  const name = match === null ? undefined : MONTH_NAMES[Number(match[1]) - 1];
  return match === null || name === undefined ? MONTHLY_NA : `${name} ${Number(match[2])}`;
}

/**
 * The Rate cell's lines. One segment is the rate alone ("5.50%"); a month whose rate changed is one short line per segment
 * ("Oct 1 to Oct 18: N/A"). No segments at all reads N/A.
 */
export function rateLines(segments: readonly Pick<StatementSegmentDto, "from" | "to" | "rateText">[]): string[] {
  if (segments.length === 0) return [MONTHLY_NA];
  const only = segments[0];
  if (segments.length === 1 && only !== undefined) return [only.rateText === "" ? MONTHLY_NA : fromServer(only.rateText)];
  return segments.map(
    (s) => `${shortDay(s.from)} to ${shortDay(s.to)}: ${s.rateText === "" ? MONTHLY_NA : fromServer(s.rateText)}`,
  );
}

/**
 * Money for a cell: "$1,069.64", thousands grouped, never wrapped (the cell is nowrap). It is the console's own money
 * formatter (`figureMoney`, the franchise list's figures); `formatCents` in money.ts has no thousands separator. An absent
 * figure reads N/A.
 */
export function moneyOrNa(cents: number | null): string {
  return figureMoney(cents ?? null);
}

/** A signed balance for Adjustments and Carried: "+$10.00", "-$5.00", "$0.00"; absent reads N/A. */
export function balanceOrNa(cents: number | null): string {
  if (cents === null) return MONTHLY_NA;
  return cents > 0 ? `+${figureMoney(cents)}` : figureMoney(cents);
}

/** The Collection cell's second line: "Debit Nov 9", or null when there is no date to show. */
export function debitLine(collection: MonthlyCollection | null): string | null {
  if (collection === null || collection.debitDate === null) return null;
  const text = shortDay(collection.debitDate);
  return text === MONTHLY_NA ? null : `Debit ${text}`;
}

const keyOf = (franchisee: string, location: string): string => `${franchisee}\u0000${location}`;

function statementFigures(
  row: StatementRowDto,
): Pick<MonthlyRow, "netCents" | "rateLines" | "royaltyDueCents" | "fundDueCents" | "fundRateText" | "inProgress" | "flatFeeText"> {
  return {
    inProgress: row.inProgress === true,
    flatFeeText: row.flatFeeText ?? null,
    netCents: row.netCents,
    rateLines: rateLines(row.segments),
    royaltyDueCents: row.royaltyDueCents,
    fundDueCents: row.fundDueCents ?? null,
    fundRateText: fromServer(row.fundRateText ?? MONTHLY_NA),
  };
}

const noCharge = {
  adjustmentCents: null,
  amountCents: null,
  carriedCents: null,
  collection: null,
  dispute: null,
  chargeId: null,
  canDispute: false,
} as const;

function statementCollection(row: StatementRowDto): MonthlyCollection | null {
  const c = row.collection;
  if (c === undefined || c === null || typeof c.label !== "string") return null;
  return { label: c.label, tone: toneOf(c.tone), debitDate: null };
}

/**
 * The franchisor's rows for one month: every statement row joined to its charge row, then any charge row the statement does
 * not have (a carried balance only) so no money drops out. A name that appears twice on either side joins nothing; both
 * rows then show on their own with N/A on the missing half, rather than one location taking another's charge.
 */
export function mergeNetworkRows(statement: RoyaltyStatementDto, ledger: NetworkLedgerDto): MonthlyRow[] {
  const statementCount = new Map<string, number>();
  for (const r of statement.rows) {
    const k = keyOf(r.franchisee, r.locationName);
    statementCount.set(k, (statementCount.get(k) ?? 0) + 1);
  }
  const ledgerCount = new Map<string, number>();
  for (const r of ledger.rows) {
    const k = keyOf(r.franchisee, r.locationName);
    ledgerCount.set(k, (ledgerCount.get(k) ?? 0) + 1);
  }
  const unique = (k: string): boolean => statementCount.get(k) === 1 && ledgerCount.get(k) === 1;
  const chargeByKey = new Map(ledger.rows.map((r) => [keyOf(r.franchisee, r.locationName), r]));
  const used = new Set<string>();
  const out: MonthlyRow[] = [];
  for (const s of statement.rows) {
    const k = keyOf(s.franchisee, s.locationName);
    const c = unique(k) ? chargeByKey.get(k) : undefined;
    if (c !== undefined) used.add(k);
    out.push({
      franchisee: s.franchisee,
      location: s.locationName,
      ...statementFigures(s),
      ...(c === undefined
        ? { ...noCharge, collection: statementCollection(s) }
        : {
            adjustmentCents: c.adjustmentCents,
            amountCents: c.amountCents,
            carriedCents: c.pendingCents,
            collection:
              c.label === null
                ? statementCollection(s)
                : { label: c.label, tone: toneOf(c.tone), debitDate: c.expectedDebitDate },
            dispute: c.dispute,
            chargeId: null,
            canDispute: false,
          }),
    });
  }
  for (const c of ledger.rows) {
    if (used.has(keyOf(c.franchisee, c.locationName))) continue;
    out.push({
      franchisee: c.franchisee,
      location: c.locationName,
      netCents: null,
      rateLines: [MONTHLY_NA],
      // The charge's own royalty due stands in when the statement has no row, so the figure is not lost.
      royaltyDueCents: c.royaltyDueCents,
      fundDueCents: c.fundDueCents ?? null,
      fundRateText: MONTHLY_NA,
      adjustmentCents: c.adjustmentCents,
      amountCents: c.amountCents,
      carriedCents: c.pendingCents,
      collection:
        c.label === null ? null : { label: c.label, tone: toneOf(c.tone), debitDate: c.expectedDebitDate },
      dispute: c.dispute,
      chargeId: null,
      canDispute: false,
    });
  }
  return out;
}

/**
 * The location's own row for the statement's month: the statement joined to the charge of that month, if any. `carried` is
 * the location's whole carried balance, the figure the next charge nets. A location with no statement has no row.
 */
export function mergeLocationRow(
  statement: RoyaltyStatementDto,
  ledger: LocationLedgerDto | null,
): MonthlyRow[] {
  const s = statement.rows[0];
  if (s === undefined) return [];
  const charge: LedgerChargeDto | undefined = ledger?.charges.find((c) => c.month === statement.month);
  const base = { franchisee: s.franchisee, location: s.locationName, ...statementFigures(s) };
  if (ledger === null || charge === undefined) {
    return [
      {
        ...base,
        ...noCharge,
        carriedCents: ledger === null ? null : ledger.pendingCents,
        collection: statementCollection(s),
      },
    ];
  }
  return [
    {
      ...base,
      adjustmentCents: charge.adjustmentCents,
      amountCents: charge.amountCents,
      carriedCents: ledger.pendingCents,
      collection: { label: charge.label, tone: toneOf(charge.tone), debitDate: charge.expectedDebitDate },
      dispute: charge.dispute,
      chargeId: charge.id,
      canDispute: charge.canDispute,
    },
  ];
}

/** The totals row: each money column is the sum of the figures the rows have; an absent figure adds nothing. */
export function monthlyTotals(rows: readonly MonthlyRow[]): MonthlyTotals {
  const sum = (pick: (r: MonthlyRow) => number | null): number =>
    rows.reduce((total, r) => total + (pick(r) ?? 0), 0);
  return {
    netCents: sum((r) => r.netCents),
    royaltyDueCents: sum((r) => r.royaltyDueCents),
    fundDueCents: sum((r) => r.fundDueCents),
    adjustmentCents: sum((r) => r.adjustmentCents),
    amountCents: sum((r) => r.amountCents),
    carriedCents: sum((r) => r.carriedCents),
  };
}

/** The dispute named by a row, with its reason, filing date and note, from the ledger's own list. */
export function findDispute(
  ledger: Pick<NetworkLedgerDto, "disputes">,
  id: string,
): NetworkDisputeDto | null {
  return (ledger.disputes ?? []).find((d) => d.id === id) ?? null;
}

/** Whether the Resolve action is offered on a row: an open dispute and the permission, nothing else. */
export function canResolveRow(row: Pick<MonthlyRow, "dispute">, holdsRoyaltyManage: boolean): boolean {
  return holdsRoyaltyManage && row.dispute !== null && row.dispute.status === "open";
}
